// Minimal WAV encoder — 16-bit signed PCM, little-endian, no
// compression. MediaRecorder emits webm/mp4 natively, so to give
// users a true .wav we decode a captured blob via the Web Audio
// API's decodeAudioData() and encode the resulting AudioBuffer
// here.
//
// Structure: 44-byte RIFF header followed by interleaved int16
// frames. Mono inputs are passed through as mono WAV. Stereo and
// anything wider are narrowed to stereo (additional channels are
// dropped — a full downmix matrix isn't worth the code for this
// feature, since Strudel's output is stereo in practice).

/** A minimal shape used so tests can feed a plain object without
 *  needing a real Web Audio AudioBuffer. */
export type WavSource = {
  sampleRate: number;
  numberOfChannels: number;
  getChannelData: (channel: number) => Float32Array;
  length: number;
};

export function encodeWav(source: WavSource): Blob {
  const channels = Math.max(1, Math.min(2, source.numberOfChannels));
  const frames = source.length;
  const sampleRate = source.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = channels * bytesPerSample;
  const dataBytes = frames * blockAlign;
  const bufferBytes = 44 + dataBytes;

  const buffer = new ArrayBuffer(bufferBytes);
  const view = new DataView(buffer);

  // RIFF header.
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');

  // fmt subchunk (16 bytes for PCM).
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // subchunk size
  view.setUint16(20, 1, true); // audio format = PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // data subchunk.
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);

  // Channel buffers. For mono we still need an array of length 1.
  const chBuffers: Float32Array[] = [];
  for (let c = 0; c < channels; c++) chBuffers.push(source.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < channels; c++) {
      const sample = chBuffers[c]![i] ?? 0;
      view.setInt16(offset, floatToInt16(sample), true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function floatToInt16(value: number): number {
  // Clip before scaling so +1.0 maps to 32767 and -1.0 to -32768.
  const clipped = Math.max(-1, Math.min(1, value));
  return clipped < 0 ? Math.round(clipped * 0x8000) : Math.round(clipped * 0x7fff);
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}
